import { DEFAULT_OPEN_PIPELINE_PHASES, TERMINAL_LEAD_STATUSES } from './utils/leadPipeline';
import type { ProjectValue } from './features/leads/utils/projectValue';

/** Eintrag Verantwortliche Person (nur Name; gemeinsamer Typ siehe director_role) */
export type CompanyResponsiblePerson = {
  name: string;
};

/** Zentrale Firmendaten (Einstellungen → Geschäftsinformationen, API `/api/settings/company`) */
export type CompanySettings = {
  company_name: string;
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
  country_code: string;
  state_code: string;
  phone: string;
  email: string;
  /** Website / Firmenhomepage */
  website: string;
  /** Ein Typ für alle verantwortlichen Personen (z. B. Geschäftsführer) */
  director_role: string;
  /** Namen der verantwortlichen Personen */
  responsible_persons: CompanyResponsiblePerson[];
  vat_id: string;
  tax_number: string;
  /** Handelsregisternummer (z. B. HRB …) */
  commercial_register_number: string;
  /** Registergericht / zuständiges Amtsgericht */
  registry_court: string;
  bank_name: string;
  bank_account_holder: string;
  iban: string;
  bic: string;
  /** Zahlungsziel in Tagen (Rechnung); kept when mode is `immediate` so switching back restores it */
  payment_terms_days: number;
  /** `immediate` = Sofort (fällig in 3 Werktagen); `net_days` uses `payment_terms_days` */
  payment_terms_mode: 'net_days' | 'immediate';
  /** Skonto in Prozent; `null` = kein Skonto */
  skonto_percent: number | null;
  /** Skonto-Frist in Tagen; `null` wenn kein Skonto */
  skonto_days: number | null;
  /** Lohnkostenanteil in Angeboten für Privatkunden ausweisen (§ 35a). */
  labor_cost_share_offer_private?: boolean;
  /** Lohnkostenanteil in Angeboten für Geschäftskunden ausweisen. */
  labor_cost_share_offer_business?: boolean;
  /** Lohnkostenanteil in Rechnungen für Privatkunden ausweisen (§ 35a). */
  labor_cost_share_invoice_private?: boolean;
  /** Lohnkostenanteil in Rechnungen für Geschäftskunden ausweisen. */
  labor_cost_share_invoice_business?: boolean;
  /** Neue Angebote übernehmen `organization.hourly_rate` in die Kalkulation. */
  inherit_hourly_rate_from_customer?: boolean;
  /** VOB-/Bau-Abzüge in Editor und Rechnungsübersicht anbieten. */
  construction_deductions_enabled?: boolean;
  /** Postfach-Import übernimmt auch Bild-Anhänge (JPG/PNG/WebP/HEIC) als Eingangsrechnung. */
  incoming_invoice_mail_images?: boolean;
  has_agb?: boolean;
  agb_filename?: string | null;
  agb_attach_private?: boolean;
  agb_attach_business?: boolean;
  has_widerrufsformular?: boolean;
  widerrufsformular_filename?: string | null;
  widerrufsformular_attach_private?: boolean;
  widerrufsformular_attach_business?: boolean;
  /** Offene Pipeline-Phasen (ohne Abgeschlossen/Verloren), Reihenfolge = Kanban links nach rechts */
  lead_pipeline_phases: string[];
  /** Nachfrist in Kalendertagen für Mahnschreiben und Mahn-E-Mails. */
  dunning_deadline_days?: number;
  /** Creditreform-Hinweis auf dem Mahnungsdokument. */
  dunning_show_creditreform?: boolean;
  /** Eingangsrechnungen: Freigabe nicht durch die erfassende Person. */
  incoming_approval_four_eyes?: boolean;
  /** Eingangsrechnungen: ab diesem Bruttobetrag (EUR) zwei Freigaben. `null` = aus. */
  incoming_approval_second_threshold?: number | null;
  /** Eingangsrechnungen: nach so vielen Tagen in Prüfung wird ein Beleg in der Liste markiert. */
  incoming_review_stale_days?: number;
  /** Ab diesem Datum (YYYY-MM-DD) gehen B2B-Rechnungen nur noch als E-Rechnung raus. `null` = keine Pflicht hinterlegt. */
  einvoice_mandatory_from?: string | null;
  /** Ziel-Marge (0–95 %) für Hinweise in der Angebotskalkulation. `null` = keine Ziel-Hinweise. */
  offer_target_margin_percent?: number | null;
  has_logo: boolean;
  updated_at: string | null;
};

/** Einstellungen → Nummernkreise (`/api/settings/document-numbering`) */
export type DocumentNumberingSettings = {
  invoice_serial_start: number | null;
  invoice_serial_start_year: string | null;
  offering_serial_start: number | null;
  offering_serial_start_year: string | null;
  current_year_suffix: string;
  invoice_observed_serial_max: number;
  offering_observed_serial_max: number;
  /** Beobachtetes Maximum der fortlaufenden Angebotsnummer (ohne Version). */
  offering_export_observed_serial_max: number;
  /** Nächste fortlaufende Angebotsnummer (ohne Version). */
  offering_export_next_serial: number;
  preview_next_invoice_reference_id: string;
  preview_next_offering_display_id: string;
  /** Voraussichtliche nächste Angebotsnummer (PDF/Versand; versioniert oder fortlaufend). */
  preview_next_offering_export_reference_id: string;
  /** Voraussichtliche nächste Nummer je Rechnungsfamilien-Typ (gemeinsamer Zähler, eigenes Präfix). */
  preview_next_invoice_by_type: {
    Rechnung: string;
    Abschlagsrechnung: string;
    Schlussrechnung: string;
    Stornorechnung: string;
    Gutschrift: string;
  };
  /** Vorgangsnummer (offering display_id): Präfix, Jahresformat, Stellenanzahl. */
  offering_number_prefix: string;
  offering_number_year_mode: 'yy' | 'year' | 'none';
  offering_number_seq_digits: number;
  offering_number_include_month: boolean;
  offering_number_delimiter: '-' | '/' | '.';
  /** Angebotsnummer (PDF/Versand): Präfix + Versionierung an/aus. */
  offering_export_prefix: string;
  offering_export_use_version: boolean;
  /** Rechnungsnummern: gemeinsames Jahresformat/Stellenanzahl, ein Präfix je Typ. */
  invoice_number_year_mode: 'yy' | 'year' | 'none';
  invoice_number_seq_digits: number;
  invoice_number_include_month: boolean;
  invoice_number_delimiter: '-' | '/' | '.';
  invoice_prefix_rechnung: string;
  invoice_prefix_abschlag: string;
  invoice_prefix_schluss: string;
  invoice_prefix_storno: string;
  invoice_prefix_gutschrift: string;
};

/** Einstellungen → Kundennummern (`/api/settings/customer-numbering`) */
export type CustomerNumberingSettings = {
  customer_number_format: string;
  preview_next_customer_number: string;
  current_year: number;
  next_seq: number;
  manual_edit_enabled: boolean;
};

/** Einstellungen → Projektnummern (`/api/settings/project-numbering`) */
export type ProjectNumberingSettings = {
  project_number_format: string;
  preview_next_project_number: string;
  current_year: number;
  next_seq: number;
  manual_edit_enabled: boolean;
};

/**
 * Legacy contact_type kept for backward compatibility with existing API
 * payloads. Ansprechpartner is removed from the product model; new UI should
 * use `ContactKind` instead.
 */
export type ContactType =
  | 'Privatkunde'
  | 'Gewerblicher Kunde'
  | 'Lieferant'
  | 'Partner'
  | 'Ansprechpartner';

/** Two kinds visible in the Kontakte tab. */
export type ContactKind = 'Privatkunde' | 'Gewerblicher Kontakt';

/** Subtype of an organization (Organisationen tab). */
export type OrganizationType = 'Gewerblicher Kunde' | 'Lieferant' | 'Partner';

/** Steuerkennzeichen des Schuldners. reverse_charge_13b erzeugt neue Belege mit vat_0_reverse_charge. */
export type TaxTreatment = 'standard' | 'reverse_charge_13b';

export const CONTACT_KINDS: ContactKind[] = ['Privatkunde', 'Gewerblicher Kontakt'];
export const ORGANIZATION_TYPES: OrganizationType[] = [
  'Gewerblicher Kunde',
  'Lieferant',
  'Partner',
];

export interface Contact {
  id: number | string;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  phone: string;
  address: string;
  company?: string | null;
  notes?: string | null;
  contact_type?: ContactType | null;
  contact_kind?: ContactKind | null;
  organization_id?: string | null;
  customer_number?: string | null;
  customer_since?: string | null;
  /** From GET detail: whether instance allows editing customer_number manually. */
  numbering_manual_edit_enabled?: boolean;
  is_deleted?: boolean;
  is_primary?: boolean;
  is_blocked?: boolean;
  block_reason?: string;
  warning_text?: string;
  tax_treatment?: TaxTreatment;
  invoice_recipient_contact_id?: string | null;
  invoice_recipient_organization_id?: string | null;
  invoice_recipient_contact?: Contact | null;
  invoice_recipient_organization?: Organization | null;
  duplicate_of_contact_id?: string | null;
  duplicate_of_organization_id?: string | null;
  duplicate_of_contact?: Pick<Contact, 'id' | 'name' | 'first_name' | 'last_name'> | null;
  duplicate_of_organization?: Pick<Organization, 'id' | 'name'> | null;
  created_at?: string;
  updated_at?: string;
}

export interface Organization {
  id: string;
  instance_id?: string;
  name: string;
  /** Firmensitz / eingetragene Adresse der Organisation (nicht Rechnungskontakt). */
  registered_address: string;
  /** USt-IdNr. des Kunden (für Reverse-Charge / innergemeinschaftliche E-Rechnungen). */
  vat_id: string;
  /** Steuernummer des Kunden (Alternative zur USt-IdNr. bei der Empfängerkennung). */
  tax_number: string;
  /** Handelsregisternummer des Kunden. */
  commercial_register_number: string;
  organization_type: OrganizationType | null;
  primary_contact_id: string | null;
  primary_contact?: Contact | null;
  billing_contact_id: string | null;
  billing_contact?: Contact | null;
  billing_email_cc: string[];
  customer_number: string | null;
  /** Unsere eigene Kundennummer bei diesem Lieferanten (nur bei Typ „Lieferant“). */
  supplier_customer_number?: string | null;
  /** From GET detail: whether instance allows editing customer_number manually. */
  numbering_manual_edit_enabled?: boolean;
  has_framework_contract: boolean;
  discount_percent: number | null;
  payment_terms_days: number | null;
  skonto_percent: number | null;
  skonto_days: number | null;
  /** Opt-in: Baunebenkosten-Umlage % für Abschlag/Schluss; null = aus. */
  site_levy_percent?: number | null;
  /** Opt-in: Sicherheitseinbehalt % auf Abschlagsrechnungen; null = aus. */
  retention_percent_partial?: number | null;
  /** Opt-in: Sicherheitseinbehalt % auf Schlussrechnung; null = aus. */
  retention_percent_final?: number | null;
  hourly_rate: number | null;
  framework_valid_until: string | null;
  origin: string;
  responsible_user_id: string | null;
  cost_center: string;
  /** DATEV (nur bei aktiver Integration): Personenkonten werden beim ersten Export vergeben. */
  datev_debitor_number?: number | null;
  datev_kreditor_number?: number | null;
  default_expense_account?: string | null;
  default_cost_center_id?: string | null;
  customer_since: string | null;
  notes: string;
  is_blocked?: boolean;
  block_reason?: string;
  warning_text?: string;
  tax_treatment?: TaxTreatment;
  invoice_recipient_contact_id?: string | null;
  invoice_recipient_organization_id?: string | null;
  invoice_recipient_contact?: Contact | null;
  invoice_recipient_organization?: Organization | null;
  duplicate_of_contact_id?: string | null;
  duplicate_of_organization_id?: string | null;
  duplicate_of_contact?: Pick<Contact, 'id' | 'name' | 'first_name' | 'last_name'> | null;
  duplicate_of_organization?: Pick<Organization, 'id' | 'name'> | null;
  iban?: string | null;
  bic?: string | null;
  bank_account_holder?: string | null;
  is_deleted?: boolean;
  created_at?: string;
  updated_at?: string;
}

/** Anzeigename: Vor- + Nachname, sonst `name`. */
export function contactDisplayName(
  c: Partial<Pick<Contact, 'first_name' | 'last_name' | 'name'>> | null | undefined
): string {
  if (!c) return '';
  const fn = (c.first_name ?? '').trim();
  const ln = (c.last_name ?? '').trim();
  const combined = [fn, ln].filter(Boolean).join(' ').trim();
  if (combined) return combined;
  return (c.name ?? '').trim();
}

/** Erste nicht-leere E-Mail: API-Spiegelung am Lead oder direkt am verknüpften Kontakt. */
export function leadOutboundEmail(
  lead:
    | {
        email?: string | null;
        contact?: Partial<Pick<Contact, 'email'>> | Partial<Pick<Contact, 'email'>>[] | null;
      }
    | null
    | undefined,
): string {
  if (!lead) return '';
  const fromLead = String(lead.email ?? '').trim();
  if (fromLead) return fromLead;
  const raw = lead.contact;
  if (!raw) return '';
  const c = Array.isArray(raw) ? raw[0] : raw;
  return String(c?.email ?? '').trim();
}

export const CONTACT_TYPES: ContactType[] = [
  'Privatkunde',
  'Gewerblicher Kunde',
  'Lieferant',
  'Partner',
  'Ansprechpartner',
];

/** Nur diese Kontaktarten dürfen einem Projekt zugeordnet werden (Auswahl + API). */
export const CONTACT_TYPES_SELECTABLE_FOR_LEAD: readonly ContactType[] = [
  'Privatkunde',
  'Gewerblicher Kunde',
];

export function isContactSelectableForLeadInquiry(contactType: string | null | undefined): boolean {
  const t = contactType != null ? String(contactType).trim() : '';
  return (CONTACT_TYPES_SELECTABLE_FOR_LEAD as readonly string[]).includes(t);
}

/** A contact is selectable as the lead's customer when it is a Privatkunde or Gewerblicher Kontakt. */
export function isContactKindSelectableForLeadInquiry(kind: string | null | undefined): boolean {
  const k = kind != null ? String(kind).trim() : '';
  return k === 'Privatkunde' || k === 'Gewerblicher Kontakt';
}

export function contactTypeRequiresCompany(t: ContactType | null | undefined): boolean {
  return t === 'Gewerblicher Kunde' || t === 'Lieferant' || t === 'Partner';
}

export function contactTypeRequiresLastName(t: ContactType | null | undefined): boolean {
  return t === 'Privatkunde' || t === 'Ansprechpartner';
}

export function contactTypeClearsCompany(t: ContactType | null | undefined): boolean {
  return t === 'Privatkunde';
}

/** Human-readable label for a contact's subtype in lists. */
export function contactKindLabel(c: Pick<Contact, 'contact_kind' | 'contact_type'>): string {
  if (c.contact_kind === 'Privatkunde') return 'Privatkunde';
  if (c.contact_kind === 'Gewerblicher Kontakt') return 'Gewerblicher Kontakt';
  if (c.contact_type === 'Privatkunde') return 'Privatkunde';
  if (c.contact_type) return 'Gewerblicher Kontakt';
  return '';
}

/** Eigenes Objekt: Baustelle / Objekt zum Lead (1:1). */
export interface LeadBaustelle {
  id?: number;
  lead_id?: string | number;
  address?: string | null;
  description?: string | null;
  /** Kunden-Bestellnummer / Auftragskennzeichen für die Baustelle. */
  bestellnummer?: string | null;
  /** Optionaler Ansprechpartner vor Ort (Monteur); Rechnung bleibt am Projektkontakt. */
  site_contact_id?: string | null;
  contact?: Contact | null;
  updated_at?: string | null;
  created_at?: string | null;
}

/** Eigenes Objekt: interne Lead-Notiz (Tab Zusammenfassung, 1:1). */
export interface LeadNotiz {
  id?: number;
  lead_id?: string | number;
  content?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
}

/** Position im Lead-Reiter „Angebot“ (Material / Leistung / frei). */
export type LeadPositionSource =
  | 'custom'
  | 'material'
  | 'leistung'
  | 'custom_text'
  | 'custom_title'
  | 'custom_free'
  | 'custom_image'
  | 'custom_surcharge';

/** Backend position row id (legacy integer or offering_positions UUID). */
export type PositionRowId = string | number;

export interface LeadPosition {
  id: PositionRowId;
  lead_id: string | number;
  sort_order: number;
  /** Laufende Positionsnummer (1-basiert) */
  pos: number;
  quantity: number;
  unit: string | null;
  /** Kurzbezeichnung Material oder Leistung */
  item_name: string;
  description: string;
  /** Nur intern sichtbar; nicht in Kunden-PDF/GAEB. */
  internal_note?: string;
  /** EK / Kosten pro Einheit (netto, wie Stammdaten net_price / net_cost) */
  unit_cost_ek: number;
  /** Aufschlag in % auf EK → VK */
  surcharge_percent: number;
  /** Verkaufspreis (VK) pro Einheit netto (aus Stammdaten gross_price bzw. EK × (1 + Zuschlag/100)) */
  unit_price_vk: number;
  /** Kalkulatorische Lohnminuten pro Mengeneinheit. */
  labor_minutes?: number;
  /** Abweichender Stundensatz; null/fehlt = Angebots-Default. */
  hourly_rate?: number | null;
  /** Positions-Gesamt netto (Menge × VK) */
  line_total: number;
  source_type: LeadPositionSource | null;
  material_id: string | null;
  leistung_id: string | null;
  /** Großhandels-/Artikelnummer (materials.gtin); nur bei Material-Positionen. */
  article_number?: string | null;
  /** Eventual-/Bedarfsposition ohne Gesamtbetrag — EP shown, line total excluded from offer sum. */
  is_eventual?: boolean;
  /** GAEB-OZ / UGL-LV-Nummer (z. B. 01.0001). */
  oz?: string | null;
  /** Titelebene 1–4; nur bei `source_type: 'custom_title'`. */
  title_level?: number | null;
  /** Eingefrorene Metallart der letzten Zuschlagsberechnung. */
  metal_kind?: 'cu' | 'al' | null;
  /** Eingefrorener Metallzuschlag je Mengeneinheit. */
  metal_surcharge?: number | null;
  /** EK inklusive Metallzuschlag zum Zeitpunkt der letzten Berechnung. */
  metal_priced_ek?: number | null;
  /** UGL-Stückliste — intern, nicht im Kunden-PDF. */
  components?: OfferingPositionComponent[];
  created_at?: string;
  updated_at?: string;
}

export type OfferingPositionComponentRole = 'u' | 'labor' | 'surcharge';

export interface OfferingPositionComponent {
  id: string;
  offering_position_id?: string;
  sort_order: number;
  article_number: string | null;
  item_name: string;
  description: string;
  quantity: number;
  unit: string | null;
  unit_cost_ek: number;
  line_total_ek: number;
  ugl_role: OfferingPositionComponentRole;
  surcharge_type: string | null;
  del_price: number | null;
}

/** Commercial offering (Angebot chain) on a project — Phase 2+. */
export interface ProjectOffering {
  id: string;
  project_id: string;
  display_id?: string | null;
  title: string;
  status?: string | null;
  offer_document_draft?: OfferDocumentDraft | null;
  copper_quote_eur_per_100kg?: number | null;
  aluminum_quote_eur_per_100kg?: number | null;
  metal_quote_source?: 'manual' | 'westmetall' | 'sonepar' | null;
  metal_quote_date?: string | null;
  metal_procurement_cost_percent?: number | null;
  created_at?: string;
  updated_at?: string;
  /** Gesetzt, wenn das Angebot zuletzt für einen Dokument-Editor gewählt wurde. */
  last_used_at?: string | null;
}

export interface Lead {
  id: string | number;
  display_id: string;
  /** From GET detail: whether instance allows editing display_id manually. */
  numbering_manual_edit_enabled?: boolean;
  /** Vom Kontakt (contact_id), nicht am Lead gespeichert. */
  name: string;
  email: string;
  phone: string;
  /** In Übersichten: Baustelle falls gesetzt, sonst Kontaktadresse (API). */
  address: string;
  /** Adresse der Baustelle / des Objekts (am Lead gespiegelt, Quelle: `baustelle`). */
  site_address?: string | null;
  /** Anzeige-Label der Kategorie; null wenn keine gesetzt. */
  category: string | null;
  /** Stabiler Schlüssel der Instanz-Kategorie. */
  category_key?: string | null;
  /** Farb-Slug der Kategorie (z. B. orange, blue). */
  category_color?: string | null;
  /** Kurztitel / Betreff des Projekts (API: lead_title). */
  lead_title: string;
  /** Anzeige-Status (Spiegel: Stage-Label oder Gewonnen/Verloren). */
  status: string;
  /** Pipeline-Phase (FK); null bei legacy-terminalen Projekten ohne Phase. */
  stage_id?: string | null;
  /** Aufgelöste Pipeline des Projekts (über Kategorie bzw. Stage). */
  pipeline_id?: string | null;
  /** Ausgang: open | won | lost */
  outcome?: 'open' | 'won' | 'lost';
  /** Projekt-/Baustellenbeschreibung (gespiegelt aus `baustelle.description`). */
  notes: string;
  ai_description: string;
  created_at: string;
  loss_reason?: string;
  loss_comment?: string;
  lost_at?: string;
  lost_by?: string;
  /** Stabile Identität des Users, der als verloren markiert hat; Rename ändert sie nicht. */
  lost_by_user_id?: string | null;
  contact_id?: string | number;
  contact?: Contact | null;
  /** Organisation des verknüpften Kontakts – nur in GET /leads/:id (Detail), nicht in der Liste. */
  organization?: Organization | null;
  baustelle?: LeadBaustelle | null;
  notiz?: LeadNotiz | null;
  /** Soft-Delete: nicht in Übersichten anzeigen (nur gesetzt, wenn die API liefert). */
  is_deleted?: boolean;
  /** Projektwert netto (Kaskade Kalkuliert → Angebot → Auftrag → Abgerechnet), = project_value.net. */
  net_total?: number;
  /** Projektwert mit Stufe und Aufteilung (aus API-Liste / Pipeline / Detail). */
  project_value?: ProjectValue | null;
  /** Gespeicherter Angebots-Entwurf (Editor); JSON aus SQLite, vom Server geparst. */
  offer_document_draft?: OfferDocumentDraft | null;
  /** Erstes/aktives Angebot (Detail-API). */
  primary_offering_id?: string | null;
  /** Alle Angebote dieses Projekts (Detail-API). */
  offerings?: ProjectOffering[];
  /** Bis zu 5 IDs aus `lead_note_history` (Reihenfolge = Anzeige oben „Angepinnt“). */
  pinned_note_history_ids?: string[];
}

/** Absatz im Angebots-Fließtext (Editor). */
export type OfferDocumentEditablePara = { id: string; text: string };

/** Zusatzposition nur im Angebots-Editor (nicht Stammdaten-Angebot-Tab). */
export type OfferExtraPositionDraft = {
  id: string;
  item_name: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price_vk: number;
};

export type OfferDraftRowKind =
  | 'apiPosition'
  | 'text'
  | 'title'
  | 'freePosition'
  | 'image'
  | 'positionSurcharge';

export type OfferPositionSurchargeMode = 'percent' | 'amount';

export type OfferPositionSurchargeConfig = {
  /** Missing or `percent` = Anteil der Zielsumme. `amount` = fester Euro-Betrag. */
  mode?: OfferPositionSurchargeMode;
  percent: number;
  /** Nur bei `mode: 'amount'`. */
  amount?: number;
  targetRowIds: string[];
};

export type OfferDraftImagePayload = {
  src: string;
  alt: string;
  filename?: string;
};

export type OfferDraftRow = {
  id: string;
  kind: OfferDraftRowKind;
  leadPositionId?: PositionRowId | null;
  sourceType?: LeadPositionSource | null;
  quantity: number;
  unit: string;
  item_name: string;
  description: string;
  internal_note?: string;
  unit_price_vk: number;
  unit_cost_ek?: number;
  surcharge_percent?: number;
  /** Kalkulatorische Lohnminuten pro Mengeneinheit. */
  labor_minutes?: number;
  /** Abweichender Stundensatz; null/fehlt = Angebots-Default. */
  hourly_rate?: number | null;
  image?: OfferDraftImagePayload | null;
  positionSurcharge?: OfferPositionSurchargeConfig | null;
  /** Eventual-/Bedarfsposition: Gesamt in parentheses, not in offer total. */
  isEventual?: boolean;
  /**
   * Freitext / Zuschlag: Lohn vs Material for § 35a.
   * Catalog Leistung/Material ignore this and use `sourceType`.
   */
  costKind?: 'neutral' | 'leistung' | 'material';
  /** Titelebene 1–4; nur bei `kind: 'title'`. Fehlt = Ebene 1. */
  titleLevel?: number;
};

/** Baustein ohne Bilddaten (src wird nicht persistiert). */
export type OfferIntroBlockPersisted =
  | { id: string; type: 'text'; content: string }
  | { id: string; type: 'title'; content: string }
  | { id: string; type: 'image'; alt: string };

/** Baustein im Angebotseditor (Bild mit src; nach Laden aus Entwurf kann src leer sein). */
export type OfferIntroBlock =
  | { id: string; type: 'text'; content: string }
  | { id: string; type: 'title'; content: string }
  | { id: string; type: 'image'; src: string; alt: string };

export interface OfferDiscount {
  type: 'percent' | 'absolute';
  value: number;
}

/** Baunebenkosten-Umlage auf Abschlag/Schluss (mindert Netto + USt). */
export type InvoiceSiteLevy = {
  kind: 'site_levy';
  label: string;
  percent?: number | null;
  amountNet?: number | null;
};

/** Sicherheitseinbehalt (mindert nur Zahlbetrag). */
export type InvoiceRetention = {
  kind: 'performance';
  percent?: number | null;
  amountGross?: number | null;
};

export type OfferVatMode =
  | 'vat_19'
  | 'vat_16'
  | 'vat_7'
  | 'vat_5'
  | 'vat_0_innergemeinschaftliche_lieferung'
  | 'vat_0_organschaft'
  | 'vat_0_photovoltaik'
  | 'vat_0_reverse_charge'
  | 'vat_0_kleinunternehmer'
  | 'vat_0_grundstuecksverkauf';

export type OfferAttachmentMimeType = 'application/pdf' | 'image/png' | 'image/jpeg';

export interface OfferAttachment {
  id: string;
  filename: string;
  mime_type: OfferAttachmentMimeType;
  object_path: string;
  size: number;
}

/** Max. Inline-Bilder (PNG/JPEG) über Intro- und Schlusstext zusammen. */
export const INLINE_DOCUMENT_IMAGE_MAX_COUNT = 12;

/** Geordneter Dokumentinhalt: Absätze, Überschriften und Bildraster. */
export type OfferDocumentContentBlock =
  | { id: string; type: 'text'; content: string }
  | { id: string; type: 'title'; content: string }
  | { id: string; type: 'imageGrid'; images: OfferAttachment[] };

/** PDF-ready content: image grids already resolved to data URLs. */
export type OfferDocumentResolvedContentBlock =
  | { id: string; type: 'text'; content: string }
  | { id: string; type: 'title'; content: string }
  | { id: string; type: 'imageGrid'; images: Array<{ id: string; src: string; alt: string }> };

/** Exact calendar day(s) or ISO calendar week(s) of service. */
export type OfferLeistungszeitraum =
  | {
      /** YYYY-MM-DD */
      from: string;
      /** YYYY-MM-DD; omit or equal to from = single day */
      to?: string;
    }
  | {
      kind: 'week';
      year: number;
      week: number;
      toYear?: number;
      toWeek?: number;
    };

/** Persistierter Stand des Angebotseditors pro Lead. */
export interface OfferDocumentDraft {
  version?: number;
  /** Welcher Dokumenttyp diesen Draft erzeugt hat (damit Templates pro Typ greifen). */
  documentType?: string;
  offerTitleRest?: string;
  /** Betreff ohne vorangestellten Dokumenttyp (manuell im Editor entfernt). */
  hideDocumentTypeInTitle?: boolean;
  leistungsort?: string;
  leistungszeitraum?: OfferLeistungszeitraum | null;
  /** Lieferschein: Lieferdatum (getrennt vom Leistungszeitraum). */
  lieferdatum?: OfferLeistungszeitraum | null;
  /** Rechnungstypen: zweite Meta-Zeile im Editor/PDF ausblenden. */
  hideLeistungszeitraum?: boolean;
  /** Lieferschein: Lieferdatum-Zeile ausblenden. */
  hideLieferdatum?: boolean;
  issueDate?: string;
  recipientNameLines?: string[];
  recipientStreetLine?: string;
  recipientPlzOrtLine?: string;
  introLetterParas?: OfferDocumentEditablePara[];
  afterTotalsParas?: OfferDocumentEditablePara[];
  /** True after the user edits the Zahlungsfrist paragraph; company/org defaults must not overwrite it. */
  paymentTermsCustomized?: boolean;
  closingGreeting?: string;
  signatureHint?: string;
  introBlocks?: OfferIntroBlockPersisted[];
  closingBlocks?: OfferIntroBlockPersisted[];
  /** Geordneter Inhalt vor der Positionstabelle (Absätze + Bildraster). */
  introContent?: OfferDocumentContentBlock[];
  /** Geordneter Inhalt nach den Summen (Absätze + Bildraster). */
  closingContent?: OfferDocumentContentBlock[];
  extraPositions?: OfferExtraPositionDraft[];
  draftRows?: OfferDraftRow[];
  discount?: OfferDiscount;
  /** Abschlag/Schluss: Baunebenkosten-Umlage (mindert Netto + USt). */
  siteLevy?: InvoiceSiteLevy;
  /** Abschlag/Schluss: Sicherheitseinbehalt (mindert nur Zahlbetrag). */
  retention?: InvoiceRetention;
  /** True after the user edits Umlage/Einbehalt; org defaults must not overwrite. */
  constructionDeductionsCustomized?: boolean;
  /** Nur für Abschlagsrechnung: Legacy-Fallback für den aktuellen Netto-Abschlag (EUR). */
  amountAlreadyInvoiced?: number;
  /** Nur für Abschlagsrechnung: aktueller Abschlags-Prozentsatz auf Basis-Netto. */
  abschlagPercent?: number;
  /** Nur für Abschlagsrechnung: Nettobetrag dieser Rechnung (EUR), Modus `amount`. */
  partialInvoiceAmount?: number;
  /** Abschlagsrechnung nach Betrag: Eingabe als Brutto (gespeichert wird weiterhin Netto). */
  partialInvoiceAmountIsGross?: boolean;
  /** Nur für Abschlagsrechnung: optionale frei editierbare Zusatzbeschreibung. */
  partialInvoiceDescription?: string;
  /** Nur für pauschale Abschlagsrechnung: optionaler Titel der automatisch erzeugten Position. */
  partialInvoiceLineName?: string;
  /** Abschlagsrechnung: Abrechnungsmodus — Prozent, Betrag oder nach Position. */
  partialInvoiceMode?: 'percent' | 'amount' | 'position';
  /**
   * Abschlagsrechnung nach Prozent/Betrag: kumuliert (bisherige Abschläge werden abgezogen)
   * oder nur dieser Abschlag. Ohne Angabe: Prozent kumuliert, Betrag nicht.
   */
  partialInvoiceCumulative?: boolean;
  /** Abschlagsrechnung: abweichende Auftragssumme netto fürs PDF (ohne Angabe: Angebotsnetto). */
  orderTotalNet?: number;
  /** Dokument-Zahlungsziel inkl. Skonto (Abschlag/Schluss); Fließtext bleibt parallel. */
  paymentTerms?: {
    mode: 'net_days' | 'immediate';
    days?: number;
    skontoPercent?: number | null;
    skontoDays?: number | null;
  };
  /** Abschlagsrechnung / Schlussrechnung: Mengen dieser Rechnung pro billingRowKey. */
  positionInvoiceQuantities?: Record<string, number>;
  vat_mode?: OfferVatMode;
  /** Zuletzt vergebene Exportlaufnummer pro Dokumenttyp-Label (Legacy; globale Nummern siehe export_reference_by_doc_type). */
  export_serial_by_doc_type?: Record<string, number>;
  /** Zuletzt vergebene, global eindeutige Dokumentennummer pro Dokumenttyp-Label (z. B. „RE-26-00207“). */
  export_reference_by_doc_type?: Record<string, string>;
  /** Anhänge, die hinter das eigentliche PDF gemerged werden (PDF/PNG/JPEG). */
  attachments?: OfferAttachment[];
  /** Standard-Anhänge (AGB / Widerrufsformular) die für diesen Vorgang nicht mitgemerged werden. */
  excluded_standard_attachments?: Array<'agb' | 'widerrufsformular'>;
  /** Wenn true: Netto-/USt-/Brutto-Summen und Rabatt-Zwischensummen im PDF und Editor ausblenden. */
  hideTotals?: boolean;
  /** Arbeitsbericht: Spalte „Artikel-Nr.“ im Editor und PDF ausblenden. */
  hideArticleNumber?: boolean;
  /** Positionstabelle: Langtext/Beschreibung unter dem Kurztext im Editor und PDF ausblenden. */
  hidePositionDescriptions?: boolean;
  /** Kundenausgabe: vollständige Positionen oder ausschließlich Titelzusammenstellung. */
  positionDetailLevel?: 'full' | 'titles';
  /** Schlussrechnung: invoice_documents.id der abzuziehenden Abschlagsrechnungen. */
  selectedDeductionInvoiceIds?: string[];
  /**
   * Zuletzt in die Rechnungsübersicht übernommene Rechnung je Dokumenttyp.
   * Nach Reload ohne neues PDF gilt sie noch als aktuelles Dokument (nicht als bisheriger Abschlag).
   */
  currentIssuedInvoiceByDocType?: Record<string, { id?: string; referenceId?: string }>;
  /** Lohnkostenanteil-Hinweis: `auto` folgt den Instanz-Einstellungen. */
  laborCostShare?: 'auto' | 'on' | 'off';
  /** Kalkulation: Verkaufspreis einer Lohnstunde zur Ermittlung des Lohnanteils. */
  calculationHourlyRate?: number;
  /** Kalkulation: tatsächliche interne Kosten einer Lohnstunde für die Marge. */
  calculationRealLaborCostRate?: number;
  /** Kalkulation: angebotsbezogene Kupfernotierung; null deaktiviert den Zuschlag. */
  calculationCopperQuoteEurPer100kg?: number | null;
  /** Kalkulation: angebotsbezogene Aluminiumnotierung; null deaktiviert den Zuschlag. */
  calculationAluminumQuoteEurPer100kg?: number | null;
  /** Kalkulation: eingefrorene Quelle der Metallnotierung dieses Angebots. */
  calculationMetalQuoteSource?: 'manual' | 'westmetall' | 'sonepar';
  /** Kalkulation: Datum der verwendeten Metallnotierung (YYYY-MM-DD). */
  calculationMetalQuoteDate?: string | null;
  /** Kalkulation: prozentuale Bezugskosten auf die Metallnotierung. */
  calculationMetalProcurementCostPercent?: number;
  /**
   * Stundensatz vom Kunden (`organization.hourly_rate`) übernehmen.
   * Fehlt das Flag (Altbestand): aus, Satz 0 — kein stilles Erben.
   */
  calculationHourlyRateFromCustomer?: boolean;
  /** Modus B: Arbeitszeit je Titel separat ausweisen statt im Positions-EP. */
  calculationLaborSeparately?: boolean;
  /** Modus B: Kurztext der Arbeitszeitzeile (Standard: Arbeitszeit). */
  calculationLaborRowLabel?: string;
  /** Modus B: überschriebener Zeilentext je Titel (`root` ohne Titel). */
  calculationLaborRowNames?: Record<string, string>;
  /** Kalkulation: ausgeblendete Margen-Hinweise (IDs je Art und Position, siehe offerMarginHints). */
  calculationDismissedHints?: string[];
}

export const LOSS_REASONS = [
  'Preis zu hoch',
  'Anderer Anbieter gewählt',
  'Projekt abgebrochen',
  'Keine Antwort vom Kunden',
  'Zeitpunkt passt nicht',
  'Sonstiges',
] as const;

export interface Stats {
  total: number;
  new: number;
  won: number;
  conversionRate: number;
}

/** Vollständige Status-Liste (offene Phasen + Terminal) — Default-Reihenfolge; live-Werte kommen aus Pipelines. */
export const STATUS_OPTIONS: string[] = [...DEFAULT_OPEN_PIPELINE_PHASES, 'Gewonnen', 'Verloren'];

export type LeadPipelineStage = {
  id: string;
  pipeline_id: string;
  label: string;
  sort_order: number;
};

export type LeadPipeline = {
  id: string;
  name: string;
  is_default: boolean;
  sort_order: number;
  stages: LeadPipelineStage[];
  category_keys: string[];
};

export const HEATING_TYPES = [
  "Wärmepumpe",
  "Gasheizung",
  "Ölheizung",
  "Solarthermie",
  "Pelletheizung",
  "Fernwärme"
];
